import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

function useHorizontalScrollFade() {
  const scrollRef = useRef(null)
  const [fadeState, setFadeState] = useState({ left: false, right: false })

  const updateFade = useCallback(() => {
    const el = scrollRef.current
    if (!el) return

    const { scrollLeft, scrollWidth, clientWidth } = el
    const canScroll = scrollWidth > clientWidth + 1

    const nextState = {
      left: canScroll && scrollLeft > 1,
      right: canScroll && scrollLeft + clientWidth < scrollWidth - 1,
    }

    setFadeState((prev) => (
      prev.left === nextState.left && prev.right === nextState.right ? prev : nextState
    ))
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    updateFade()

    const resizeObserver = new ResizeObserver(updateFade)
    resizeObserver.observe(el)

    const mutationObserver = new MutationObserver(updateFade)
    mutationObserver.observe(el, { childList: true, subtree: true })

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [updateFade])

  return { scrollRef, fadeState, onScroll: updateFade }
}

export default function MediaListContainer({ children, className, fadeColor = 'white' }) {
  const { scrollRef, fadeState, onScroll } = useHorizontalScrollFade()

  return (
    <div
      className={classnames('media-list-wrapper', className, {
        'media-list-wrapper--fade-background': fadeColor === 'background',
      })}
    >
      <div ref={scrollRef} className="media-list" onScroll={onScroll}>
        {children}
      </div>
      <div
        className={classnames('media-list__fade media-list__fade--left', { 'is-visible': fadeState.left })}
        aria-hidden="true"
      />
      <div
        className={classnames('media-list__fade media-list__fade--right', { 'is-visible': fadeState.right })}
        aria-hidden="true"
      />
    </div>
  )
}

MediaListContainer.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  fadeColor: PropTypes.oneOf(['white', 'background']),
}
